"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Car, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Cpu, ShieldCheck, Sparkles, Headphones, ArrowRight, ClipboardCheck, MessageSquare, Compass, Wrench, House as Home, BookOpen } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
  default?: any;
  options?: Array<{
    value: string;
    label: string;
    price?: number;
  }>;
  config?: Record<string, any>;
  validation?: {
    messages?: Record<string, string>;
  };
}
interface FormDefinition {
  id?: number;
  key: string;
  name: string;
  description?: string;
  availability?: {
    accepting: boolean;
    state: string;
    message?: string;
  };
  fields: FormFieldDefinition[];
  submit?: {
    label: string;
    processing_label: string;
  };
  spam: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
    challenge?: any;
  };
}
const copy = {
  eyebrow: {
    vi: "TIẾP CẬN PHÒNG LAB // QUY TRÌNH HỌC THỬ",
    en: "LAB ACCESS // TRIAL PROTOCOL"
  },
  heading: {
    vi: "Đăng ký buổi trải nghiệm học thử miễn phí",
    en: "Book a complimentary trial session"
  },
  subheading: {
    vi: "Trải nghiệm trực tiếp không gian phòng lab Robotics chuẩn quốc tế. Trong 90 phút tương tác thực tế, học sinh sẽ tự tay lắp ráp mô hình cơ khí hoạt động, lập trình thuật toán điều khiển và giải quyết bài toán Engineering cùng các chuyên gia công nghệ giàu kinh nghiệm.",
    en: "Experience our Robotics lab in person. Over 90 hands-on minutes, your child constructs an active robotic mechanism, writes algorithmic control logic, and solves real Engineering challenges with qualified industry mentors."
  },
  inclusions: {
    ratio: {
      vi: "Tỷ lệ 1 Mentor : 6 Học sinh",
      en: "1:6 Mentor Ratio"
    },
    hardware: {
      vi: "Bộ công cụ Robotics & Lab chuẩn bị sẵn",
      en: "Lab Hardware & Kits Included"
    },
    aptitude: {
      vi: "Đánh giá tư duy & năng lực STEM/AI",
      en: "Aptitude & STEM Evaluation"
    }
  },
  processEyebrow: {
    vi: "LỘ TRÌNH 4 BƯỚC TIẾP NHẬN",
    en: "4-STAGE ONBOARDING FLOW"
  },
  processHeading: {
    vi: "Quy trình sau khi đăng ký học thử",
    en: "What happens after you register"
  },
  processSteps: [{
    num: "01",
    title: {
      vi: "ĐĂNG KÝ",
      en: "REGISTER"
    },
    desc: {
      vi: "Điền biểu mẫu thông tin học sinh và chọn cơ sở phòng lab phù hợp.",
      en: "Submit student details and select your preferred campus location."
    },
    icon: ClipboardCheck
  }, {
    num: "02",
    title: {
      vi: "ROBOTO LIÊN HỆ TƯ VẤN",
      en: "ROBOTO CONTACTS YOU"
    },
    desc: {
      vi: "Chuyên viên học vụ trao đổi sở thích và xác nhận khung giờ trải nghiệm.",
      en: "Admissions coordinator discusses learning goals and confirms your session."
    },
    icon: MessageSquare
  }, {
    num: "03",
    title: {
      vi: "TÌM HIỂU CHƯƠNG TRÌNH",
      en: "EXPLORE PROGRAMS"
    },
    desc: {
      vi: "Phụ huynh nhận tài liệu lộ trình đào tạo và định hướng kỹ năng theo lứa tuổi.",
      en: "Receive curriculum preview materials matched to child age and aptitude."
    },
    icon: Compass
  }, {
    num: "04",
    title: {
      vi: "TRẢI NGHIỆM",
      en: "TRIAL SESSION"
    },
    desc: {
      vi: "Tham gia trực tiếp 90 phút thực hành lắp ráp cơ khí & lập trình tại phòng lab.",
      en: "Attend 90 minutes of hands-on robotics engineering in the academy lab."
    },
    icon: Wrench
  }],
  hotlineBadge: {
    vi: "Cần tư vấn ngay?",
    en: "Need quick guidance?"
  },
  hotlineCall: {
    vi: "Hotline: 0346 386 986",
    en: "Hotline: 0346 386 986"
  },
  hotlineSub: {
    vi: "Tư vấn lộ trình học Robotics & Coding trực tiếp cùng ban chuyên môn",
    en: "Direct academic advisory for Robotics & Coding learning tracks"
  },
  connecting: {
    vi: "Đang kết nối hệ thống đặt lịch phòng lab...",
    en: "Connecting to lab booking system..."
  },
  systemNotice: {
    vi: "Thông báo hệ thống đăng ký",
    en: "Booking system notice"
  },
  directCallNotice: {
    vi: "Quý phụ huynh vui lòng gọi trực tiếp Hotline 0346 386 986 để giữ lịch trải nghiệm ngay.",
    en: "Please telephone us directly at Hotline 0346 386 986 to schedule your trial slot immediately."
  },
  successTitle: {
    vi: "Đã ghi nhận yêu cầu trải nghiệm học thử",
    en: "Trial reservation received"
  },
  successDefaultMsg: {
    vi: "Yêu cầu đăng ký buổi học thử Robotics & Coding của bạn đã được xác nhận thành công. Chuyên viên tư vấn học vụ STEM sẽ liên hệ hỗ trợ trong thời gian sớm nhất.",
    en: "Your complimentary trial reservation has been confirmed. A STEM admissions mentor will call you shortly."
  },
  locationLabel: {
    vi: "Địa điểm // Phòng Lab ROBOTO Innovation Hub",
    en: "Location // ROBOTO Innovation Hub Lab"
  },
  bookAnother: {
    vi: "Đăng ký thêm buổi khác",
    en: "Book another session"
  },
  backHome: {
    vi: "Về trang chủ",
    en: "Back to Home"
  },
  explorePrograms: {
    vi: "Khám phá chương trình",
    en: "Explore Programs"
  },
  submitBtn: {
    vi: "Đăng Ký Buổi Trải Nghiệm Miễn Phí",
    en: "Request Complimentary Trial"
  },
  submittingBtn: {
    vi: "Đang bảo lưu chỗ tại phòng Lab...",
    en: "Securing lab slot..."
  },
  disclaimer: {
    vi: "Hoàn toàn miễn phí, không phát sinh chi phí hay yêu cầu thanh toán thẻ. Buổi học diễn ra trực tiếp tại phòng lab chuyên dụng.",
    en: "No commitment or credit card required. Sessions are held in working Robotics laboratories."
  },
  contactEyebrow: {
    vi: "THÔNG TIN LIÊN HỆ TRỰC TIẾP",
    en: "CONTACT PROTOCOL"
  },
  contactHeading: {
    vi: "Văn phòng Tuyển sinh & Hỗ trợ Phụ huynh",
    en: "Admissions & Center Desk"
  },
  hotlineLabel: {
    vi: "Hotline Tiếp Nhận Nhanh",
    en: "Direct Voice Hotline"
  },
  hotlineDesc: {
    vi: "Giải đáp lộ trình đào tạo, lịch học và kiểm tra đầu vào",
    en: "Dedicated parent inquiry desk & admissions team"
  },
  emailLabel: {
    vi: "Hòm Thư Tiếp Nhận Yêu Cầu",
    en: "Inquiry Dispatch"
  },
  emailDesc: {
    vi: "Thời gian phản hồi thông thường dưới 3 giờ làm việc",
    en: "Average response time under 3 business hours"
  },
  hoursLabel: {
    vi: "Giờ Mở Cửa Phòng Lab & Trung Tâm",
    en: "Laboratory Operating Hours"
  },
  hoursWeekday: {
    vi: "Thứ Hai - Thứ Sáu: 08:30 - 18:00",
    en: "Monday to Friday: 9am - 6pm"
  },
  hoursSaturday: {
    vi: "Thứ Bảy & Chủ Nhật: 08:00 - 17:30 (Các ca trải nghiệm & lớp chuyên sâu)",
    en: "Saturday: 10am - 4pm (Trial & Competition Workshops)"
  },
  hoursClosed: {
    vi: "Nghỉ các ngày Lễ & Tết theo quy định",
    en: "Closed on Public Holidays"
  },
  campusEyebrow: {
    vi: "TỌA ĐỘ PHÒNG LAB",
    en: "ACADEMY COORDINATES"
  },
  campusHeading: {
    vi: "Hệ Thống Phòng Lab ROBOTO",
    en: "Innovation Lab Campus"
  },
  tabMain: {
    vi: "Lab Trung Tâm",
    en: "Central Lab"
  },
  tabWest: {
    vi: "Lab Phía Tây",
    en: "West Lab"
  },
  campusAddressMain: {
    vi: "Tòa nhà Công nghệ ROBOTO, Khu Đô thị Mới, Cầu Giấy, Hà Nội",
    en: "ROBOTO Technology Tower, Innovation Quarter, Tech City"
  },
  campusAddressWest: {
    vi: "Tổ hợp Sáng tạo STEAM, Khu Đô thị Nam Từ Liêm, Hà Nội",
    en: "STEAM Innovation Hub, Circuit Boulevard, West Campus"
  },
  campusSuiteMain: {
    vi: "Tầng 1 Trung tâm STEM // Phòng Lab Robotics & AI Chuyên Sâu",
    en: "Ground Level STEM Hub // High Bay Engineering Suite"
  },
  campusSuiteWest: {
    vi: "Khu Vực Nghiên cứu Cơ điện tử // Không gian Coding & LEGO Education",
    en: "Mechatronics Lab Wing // Coding & LEGO Education Space"
  },
  parkingTitle: {
    vi: "Khu vực đưa đón phụ huynh & Bãi đỗ xe an toàn",
    en: "Parent Drop-off & Dedicated Parking"
  },
  parkingDesc: {
    vi: "Có khu dừng đón 15 phút ngay sảnh chính. Bãi đỗ xe ô tô và xe máy miễn phí dành riêng cho phụ huynh đưa đón con học thử.",
    en: "Reserved 30-minute visitor spaces at Gate B. Underground parking complimentary for trial attendees with validated lab pass."
  }
};
const friendlyFieldValidationText = (fieldKey: string, fieldLabel: string, lang: "vi" | "en"): string => {
  const lk = (fieldKey + " " + fieldLabel).toLowerCase();
  if (lk.includes("parent") || lk.includes("guardian") || lk.includes("phụ huynh")) {
    return lang === "vi" ? "Vui lòng nhập họ và tên phụ huynh." : "Please enter parent full name.";
  }
  if (lk.includes("student") || lk.includes("child") || lk.includes("học sinh")) {
    return lang === "vi" ? "Vui lòng nhập họ và tên học sinh." : "Please enter student full name.";
  }
  if (lk.includes("age") || lk.includes("grade") || lk.includes("tuổi")) {
    return lang === "vi" ? "Vui lòng nhập độ tuổi của con." : "Please enter student age.";
  }
  if (lk.includes("phone") || lk.includes("tel") || lk.includes("điện thoại")) {
    return lang === "vi" ? "Vui lòng nhập số điện thoại liên hệ hợp lệ." : "Please enter a valid contact phone number.";
  }
  if (lk.includes("email")) {
    return lang === "vi" ? "Vui lòng nhập địa chỉ email hợp lệ." : "Please enter a valid email address.";
  }
  if (lk.includes("program") || lk.includes("chương trình")) {
    return lang === "vi" ? "Vui lòng chọn chương trình con quan tâm." : "Please select a program of interest.";
  }
  if (lk.includes("center") || lk.includes("campus") || lk.includes("cơ sở")) {
    return lang === "vi" ? "Vui lòng chọn cơ sở phòng lab mong muốn." : "Please select a preferred lab campus.";
  }
  return lang === "vi" ? "Vui lòng hoàn thiện trường thông tin này." : "Please complete this field.";
};
const fieldI18n: Record<string, {
  label: {
    vi: string;
    en: string;
  };
  placeholder?: {
    vi: string;
    en: string;
  };
  help?: {
    vi: string;
    en: string;
  };
}> = {
  parent_name: {
    label: {
      vi: "Họ và tên phụ huynh",
      en: "Parent Full Name"
    },
    placeholder: {
      vi: "Ví dụ: Nguyễn Văn An",
      en: "e.g. Robert Smith"
    }
  },
  student_name: {
    label: {
      vi: "Họ và tên học sinh",
      en: "Student Full Name"
    },
    placeholder: {
      vi: "Ví dụ: Nguyễn Minh Khang",
      en: "e.g. Leo Smith"
    }
  },
  student_age: {
    label: {
      vi: "Độ tuổi của con (4 - 16 tuổi)",
      en: "Student Age (4–16 yrs)"
    },
    placeholder: {
      vi: "Ví dụ: 8 tuổi",
      en: "e.g. 8 years old"
    }
  },
  phone: {
    label: {
      vi: "Số điện thoại liên hệ",
      en: "Contact Phone Number"
    },
    placeholder: {
      vi: "Ví dụ: 0912 345 678",
      en: "e.g. (555) 987-6543"
    }
  },
  email: {
    label: {
      vi: "Địa chỉ Email",
      en: "Email Address"
    },
    placeholder: {
      vi: "phuhuynh@example.com",
      en: "parent@example.com"
    }
  },
  program_of_interest: {
    label: {
      vi: "Chương trình quan tâm",
      en: "Program of Interest"
    },
    placeholder: {
      vi: "Chọn chương trình (Robotics, Coding, STEM...)",
      en: "Select a program"
    }
  },
  preferred_center: {
    label: {
      vi: "Cơ sở phòng lab mong muốn",
      en: "Preferred Lab Campus"
    },
    placeholder: {
      vi: "Chọn cơ sở gần nhất",
      en: "Select nearest campus"
    }
  },
  notes: {
    label: {
      vi: "Ghi chú thêm về sở thích / kinh nghiệm của con",
      en: "Additional Notes / Prior Experience"
    },
    placeholder: {
      vi: "Con đã từng học Robotics, LEGO hoặc lập trình chưa?",
      en: "Has your child built with LEGO or tried Coding?"
    }
  }
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
export default function Trial() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "vi" || stored === "en") return stored;
    }
    return "vi";
  });
  useEffect(() => {
    const handleCustomChange = (e: any) => {
      const incoming = e?.detail?.lang;
      if (incoming === "vi" || incoming === "en") {
        setLang(incoming);
      }
    };
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [honeypotVal, setHoneypotVal] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const [activeCampus, setActiveCampus] = useState<string>("main");
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setLoadError(lang === "vi" ? "Đang khởi tạo dịch vụ kết nối. Vui lòng tải lại trang." : "Service initialization pending. Please refresh.");
      setLoading(false);
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/free_trial_request").then((data: FormDefinition) => {
      if (!isMounted) return;
      setFormDef(data);
      setFetchTimestamp(Date.now());
      const initials: Record<string, string> = {};
      if (Array.isArray(data.fields)) {
        data.fields.forEach(f => {
          if (f.default !== undefined && f.default !== null) {
            initials[f.id] = String(f.default);
          } else {
            initials[f.id] = "";
          }
        });
      }
      setFieldValues(initials);
      setLoading(false);
    }).catch((err: any) => {
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((listData: any) => {
          if (!isMounted) return;
          const items = listData?.items || [];
          const matched = items.find((i: any) => i.key === "free_trial_request") || (items.length === 1 ? items[0] : null) || items.find((i: any) => i.name?.toLowerCase().includes("trial") || i.name?.toLowerCase().includes("free trial"));
          if (matched?.key) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
          }
          throw new Error("Form not found");
        }).then((matchedData: FormDefinition) => {
          if (!isMounted) return;
          setFormDef(matchedData);
          setFetchTimestamp(Date.now());
          const initials: Record<string, string> = {};
          if (Array.isArray(matchedData.fields)) {
            matchedData.fields.forEach(f => {
              initials[f.id] = f.default ? String(f.default) : "";
            });
          }
          setFieldValues(initials);
          setLoading(false);
        }).catch(() => {
          if (isMounted) {
            setLoadError(lang === "vi" ? "Hiện chưa thể tải mẫu đăng ký phòng lab lúc này." : "Unable to retrieve booking form details at this time.");
            setLoading(false);
          }
        });
      } else {
        if (isMounted) {
          setLoadError(lang === "vi" ? "Mẫu đăng ký học thử hiện đang tạm bảo trì." : "Booking form is currently unavailable.");
          setLoading(false);
        }
      }
    });
    return () => {
      isMounted = false;
    };
  }, [lang]);
  const handleInputChange = (fieldId: string, val: string) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: val
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || submitting) return;
    setGeneralError(null);
    setFieldErrors({});
    const minSeconds = formDef.spam?.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsedSeconds) * 1000)));
    }
    setSubmitting(true);
    const client = getWvcClient();
    try {
      const payload: any = {
        values: fieldValues,
        token: formDef.spam.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: honeypotVal
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml(res.confirmation?.message || `<p>${copy.successDefaultMsg[lang]}</p>`);
        }
      } else {
        if (res?.code === "frms_validation_failed" && res?.errors) {
          const errs: Record<string, string> = {};
          Object.entries(res.errors).forEach(([k, errObj]: [string, any]) => {
            const currentField = formDef.fields.find(f => f.id === k);
            const friendly = friendlyFieldValidationText(currentField?.key || "", currentField?.label || "", lang);
            errs[k] = errObj?.message || friendly;
          });
          setFieldErrors(errs);
          setGeneralError(lang === "vi" ? "Vui lòng kiểm tra lại các trường thông tin được đánh dấu." : "Please review the highlighted fields.");
        } else if (res?.code === "frms_invalid_token") {
          const refreshed = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}`);
          setFormDef(refreshed);
          setFetchTimestamp(Date.now());
          setGeneralError(lang === "vi" ? "Phiên làm việc vừa được làm mới. Quý phụ huynh vui lòng bấm gửi lại yêu cầu." : "Session refreshed. Please submit your request once more.");
        } else {
          setGeneralError(res?.message || (lang === "vi" ? "Có lỗi xảy ra trong quá trình gửi. Vui lòng kiểm tra lại thông tin và thử lại." : "An unexpected error occurred. Please check your information and try again."));
        }
      }
    } catch (err: any) {
      setGeneralError(err?.message || (lang === "vi" ? "Không thể gửi yêu cầu lúc này. Vui lòng kiểm tra kết nối mạng hoặc liên hệ trực tiếp hotline." : "Unable to send your request. Please check your connection or contact our hotline directly."));
    } finally {
      setSubmitting(false);
    }
  };
  const getFieldMeta = (field: FormFieldDefinition) => {
    const matched = fieldI18n[field.key];
    if (matched) {
      return {
        label: matched.label[lang] || field.label,
        placeholder: matched.placeholder?.[lang] || field.placeholder || "",
        help: matched.help?.[lang] || field.help || ""
      };
    }
    const lk = field.label.toLowerCase();
    if (lk.includes("parent") || lk.includes("guardian")) {
      return {
        label: lang === "vi" ? "Họ và tên phụ huynh" : field.label,
        placeholder: lang === "vi" ? "Ví dụ: Nguyễn Văn An" : field.placeholder || "",
        help: field.help || ""
      };
    }
    if (lk.includes("student") || lk.includes("child") || lk.includes("kid")) {
      return {
        label: lang === "vi" ? "Họ và tên học sinh" : field.label,
        placeholder: lang === "vi" ? "Ví dụ: Nguyễn Minh Khang" : field.placeholder || "",
        help: field.help || ""
      };
    }
    if (lk.includes("age") || lk.includes("grade") || lk.includes("birth")) {
      return {
        label: lang === "vi" ? "Độ tuổi của con (4 - 16 tuổi)" : field.label,
        placeholder: lang === "vi" ? "Ví dụ: 8 tuổi" : field.placeholder || "",
        help: field.help || ""
      };
    }
    if (lk.includes("phone") || lk.includes("mobile") || lk.includes("tel")) {
      return {
        label: lang === "vi" ? "Số điện thoại liên hệ" : field.label,
        placeholder: lang === "vi" ? "Ví dụ: 0912 345 678" : field.placeholder || "",
        help: field.help || ""
      };
    }
    if (lk.includes("email")) {
      return {
        label: lang === "vi" ? "Địa chỉ Email" : field.label,
        placeholder: lang === "vi" ? "phuhuynh@example.com" : field.placeholder || "",
        help: field.help || ""
      };
    }
    if (lk.includes("program") || lk.includes("course") || lk.includes("track")) {
      return {
        label: lang === "vi" ? "Chương trình quan tâm" : field.label,
        placeholder: lang === "vi" ? "Chọn chương trình (Robotics, Coding, STEM...)" : field.placeholder || "",
        help: field.help || ""
      };
    }
    if (lk.includes("center") || lk.includes("campus") || lk.includes("location")) {
      return {
        label: lang === "vi" ? "Cơ sở phòng lab mong muốn" : field.label,
        placeholder: lang === "vi" ? "Chọn cơ sở gần nhất" : field.placeholder || "",
        help: field.help || ""
      };
    }
    if (lk.includes("note") || lk.includes("message") || lk.includes("comment")) {
      return {
        label: lang === "vi" ? "Ghi chú thêm về sở thích / kinh nghiệm của con" : field.label,
        placeholder: lang === "vi" ? "Con đã từng học Robotics, LEGO hoặc lập trình chưa?" : field.placeholder || "",
        help: field.help || ""
      };
    }
    return {
      label: field.label,
      placeholder: field.placeholder || "",
      help: field.help || ""
    };
  };
  return <section id="book-a-free-trial" data-nav="light" className="relative bg-background text-foreground py-16 lg:py-24 border-b border-border">
      {/* Decorative blueprint pattern overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.025]" style={{
      backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
      backgroundSize: "28px 28px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-16">
        {/* Technical stage eyebrow / Spec rail */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-2 w-2 rounded-xs bg-secondary" />
          <div className="h-px w-10 bg-secondary" />
          <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">
            {copy.eyebrow[lang]}
          </span>
        </div>

        {/* Explicit Two-Column Desktop Layout (Stacking on Mobile) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* ================= LEFT COLUMN ================= */}
          {/* (1) Visual Image, (2) Value Proposition Copy, (3) Inclusions Checklist, (4) 4-Step Process Strip */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6 flex flex-col gap-6">
            {/* Value Proposition Copy */}
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.14]">
                {copy.heading[lang]}
              </h2>
              <p className="mt-4 font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
                {copy.subheading[lang]}
              </p>
            </div>

            {/* Visual Photography Element (Robotics lab engineering environment) */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-sm group">
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_robotics_circuits_assembly_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_robotics_circuits_assembly_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_robotics_circuits_assembly_lab.png-wvc-sizes" alt={lang === "vi" ? "Học sinh thực hành lắp ráp robot và điều khiển mạch điện tử trong phòng lab ROBOTO" : "Students assembling robotics mechanisms and testing electronic circuits in ROBOTO academy lab"} loading="lazy" decoding="async" className="w-full h-full object-cover object-center group-hover:scale-102 transition-transform duration-500 ease-out" />
                {/* Precision blueprint coordinate tags overlay */}
                <div className="absolute top-3 left-3 flex items-center gap-2 bg-background/90 backdrop-blur-sm border border-border px-2.5 py-1 rounded-md text-[11px] font-mono text-foreground shadow-xs">
                  <span className="size-2 rounded-xs bg-secondary animate-pulse" />
                  <span>ROBOTO LAB // HARDWARE LAB 01</span>
                </div>
                <div className="absolute bottom-3 right-3 bg-background/90 backdrop-blur-sm border border-border px-2.5 py-1 rounded-md text-[11px] font-mono text-muted-foreground shadow-xs">
                  90-MIN HANDS-ON
                </div>
              </div>
            </div>

            {/* Inclusions Checklist Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex items-center gap-2.5 p-3 rounded-md bg-muted/60 border border-border">
                <div className="size-6 rounded-md bg-secondary/15 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="size-3.5 text-secondary" />
                </div>
                <span className="text-xs font-medium text-foreground">
                  {copy.inclusions.ratio[lang]}
                </span>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-md bg-muted/60 border border-border">
                <div className="size-6 rounded-md bg-accent/15 flex items-center justify-center shrink-0">
                  <Cpu className="size-3.5 text-accent" />
                </div>
                <span className="text-xs font-medium text-foreground">
                  {copy.inclusions.hardware[lang]}
                </span>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-md bg-muted/60 border border-border">
                <div className="size-6 rounded-md bg-secondary/15 flex items-center justify-center shrink-0">
                  <Sparkles className="size-3.5 text-secondary" />
                </div>
                <span className="text-xs font-medium text-foreground">
                  {copy.inclusions.aptitude[lang]}
                </span>
              </div>
            </div>

            {/* 4-Step 'What Happens After You Register' Visual Process Strip */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-xs bg-secondary" />
                  <span className="font-mono text-xs font-semibold text-secondary uppercase tracking-wider">
                    {copy.processEyebrow[lang]}
                  </span>
                </div>
                <span className="text-xs font-mono text-muted-foreground">
                  STEP 01 → 04
                </span>
              </div>

              <h3 className="font-serif text-lg font-bold text-foreground mt-3 mb-4">
                {copy.processHeading[lang]}
              </h3>

              {/* Connected Blueprint / Circuit Motif Step Strip */}
              <div className="relative flex flex-col gap-3">
                {copy.processSteps.map((step, sIdx) => {
                const Icon = step.icon;
                return <div key={step.num} data-index={sIdx} className="relative flex items-start gap-3.5 p-3 rounded-lg bg-muted/40 border border-border/80 hover:border-secondary/50 transition-colors">
                      {/* Step index pill with circuit terminal styling */}
                      <div className="size-9 rounded-md bg-card border border-border flex items-center justify-center shrink-0 text-secondary font-mono font-bold text-xs shadow-2xs">
                        <Icon className="size-4 text-secondary" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-secondary">
                            {step.num}
                          </span>
                          <span className="font-serif text-xs font-bold uppercase tracking-wide text-foreground">
                            {step.title[lang]}
                          </span>
                        </div>
                        <p className="font-sans text-xs text-muted-foreground mt-0.5 leading-relaxed">
                          {step.desc[lang]}
                        </p>
                      </div>

                      {sIdx < copy.processSteps.length - 1 && <div className="hidden sm:flex items-center justify-center self-center text-muted-foreground/40">
                          <ArrowRight className="size-3.5" />
                        </div>}
                    </div>;
              })}
              </div>
            </div>
          </motion.div>

          {/* ================= RIGHT COLUMN ================= */}
          {/* Dynamic Form Container + Direct Contact & Hotline Area */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6 flex flex-col gap-6 relative">
            {/* Quick Hotline Guidance Banner */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 sm:p-4 rounded-lg bg-secondary/10 border border-secondary/25">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-md bg-secondary/20 flex items-center justify-center text-secondary shrink-0">
                  <Headphones className="size-4.5" />
                </div>
                <div>
                  <span className="text-xs font-mono uppercase text-secondary font-semibold block">
                    {copy.hotlineBadge[lang]}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {copy.hotlineSub[lang]}
                  </p>
                </div>
              </div>
              <a href="tel:0346386986" className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-md bg-card border border-secondary/30 text-secondary font-mono font-bold text-sm tracking-wide hover:bg-secondary hover:text-secondary-foreground transition-colors shrink-0 shadow-2xs">
                <Phone className="size-3.5" />
                <span>{copy.hotlineCall[lang]}</span>
              </a>
            </div>

            {/* Dynamic Form Container */}
            <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm relative">
              {/* Static Floating Decorative Mascot (Placed safely outside interactive bounds with zero overlap on form inputs) */}
              <div className="hidden lg:block absolute -top-12 -right-8 xl:-right-16 2xl:-right-20 w-36 lg:w-40 xl:w-48 z-10 pointer-events-none select-none" aria-hidden="true">
                <div className="transform rotate-2 transition-transform duration-300">
                  <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} loading="lazy" decoding="async" className="w-full h-auto object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
                </div>
              </div>

              {loading && <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                  <Loader2 className="size-7 animate-spin text-secondary" />
                  <p className="text-sm font-sans">{copy.connecting[lang]}</p>
                </div>}

              {!loading && loadError && <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
                  <AlertCircle className="size-5 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold">{copy.systemNotice[lang]}</p>
                    <p className="mt-1 text-destructive/90">{loadError}</p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {copy.directCallNotice[lang]}
                    </p>
                  </div>
                </div>}

              {/* Confirmation / Success State with Expanded Action Buttons */}
              {!loading && !loadError && confirmationHtml && <motion.div initial={shouldReduceMotion ? false : {
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="p-8 text-center flex flex-col items-center gap-4 bg-muted/30 rounded-lg border border-border">
                  <div className="size-14 rounded-lg bg-secondary/15 flex items-center justify-center text-secondary">
                    <CheckCircle2 className="size-8" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-foreground">
                    {copy.successTitle[lang]}
                  </h3>
                  <div className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed max-w-md" dangerouslySetInnerHTML={{
                __html: confirmationHtml
              }} />
                  <div className="pt-5 border-t border-border w-full flex flex-col items-center gap-3">
                    <span className="text-xs font-mono uppercase text-muted-foreground">
                      {copy.locationLabel[lang]}
                    </span>

                    {/* Action buttons cluster: Book another + Back to Home + Explore Programs */}
                    <div className="flex flex-wrap items-center justify-center gap-2.5 w-full pt-2">
                      <Button variant="outline" size="sm" className="text-xs font-serif" onClick={() => {
                    setConfirmationHtml(null);
                    setFieldValues({});
                  }}>
                        {copy.bookAnother[lang]}
                      </Button>

                      <Button asChild variant="outline" size="sm" className="text-xs font-serif">
                        <Link to="/">
                          <Home className="size-3.5" />
                          <span>{copy.backHome[lang]}</span>
                        </Link>
                      </Button>

                      <Button asChild size="sm" className="text-xs font-serif bg-primary text-primary-foreground hover:bg-primary/90">
                        <Link to="/ProgramsPage">
                          <BookOpen className="size-3.5" />
                          <span>{copy.explorePrograms[lang]}</span>
                        </Link>
                      </Button>
                    </div>
                  </div>
                </motion.div>}

              {/* Live Form */}
              {!loading && !loadError && !confirmationHtml && formDef && <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  {generalError && <div className="p-3.5 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2.5">
                      <AlertCircle className="size-4 shrink-0" />
                      <span>{generalError}</span>
                    </div>}

                  {/* Honeypot decoy field if configured by spam module */}
                  {formDef.spam.honeypot && <div style={{
                position: "absolute",
                left: "-9999px",
                top: "-9999px",
                opacity: 0
              }} aria-hidden="true">
                      <input type="text" name={formDef.spam.honeypot.field} value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} tabIndex={-1} autoComplete="off" />
                    </div>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Render fields directly from fetched definition */}
                    {formDef.fields?.map((field, idx) => {
                  const meta = getFieldMeta(field);
                  const isFullWidth = field.type === "textarea" || field.key === "program_of_interest" || field.key === "preferred_center" || field.key === "notes" || field.label.toLowerCase().includes("program") || field.label.toLowerCase().includes("center") || field.label.toLowerCase().includes("notes");
                  return <div key={field.id} data-index={idx} className={`space-y-1.5 ${isFullWidth ? "sm:col-span-2" : "col-span-1"}`}>
                          <Label htmlFor={field.id} className="font-serif text-xs uppercase tracking-wider text-foreground font-semibold flex items-center justify-between">
                            <span>
                              {meta.label}
                              {field.required && <span className="text-accent ml-1">*</span>}
                            </span>
                            {meta.help && <span className="text-[11px] font-sans font-normal text-muted-foreground">
                                {meta.help}
                              </span>}
                          </Label>

                          {field.type === "select" ? <Select value={fieldValues[field.id] || ""} onValueChange={val => handleInputChange(field.id, val)}>
                              <SelectTrigger id={field.id} className="w-full h-11 bg-background border-input text-foreground text-sm focus-visible:border-ring focus-visible:ring-ring/40">
                                <SelectValue placeholder={meta.placeholder || (lang === "vi" ? "Vui lòng chọn một tùy chọn" : "Select an option")} />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options?.map((opt, optIndex) => <SelectItem key={opt.value} data-index={optIndex} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>)}
                              </SelectContent>
                            </Select> : <Input id={field.id} type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : field.type === "number" ? "number" : "text"} placeholder={meta.placeholder} value={fieldValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} required={field.required} className="h-11 bg-background border-input text-foreground text-sm placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/40" />}

                          {fieldErrors[field.id] && <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                              <AlertCircle className="size-3 shrink-0" />
                              <span>{fieldErrors[field.id]}</span>
                            </p>}
                        </div>;
                })}
                  </div>

                  <div className="pt-2">
                    <Button type="submit" disabled={submitting} className="w-full h-12 bg-accent text-accent-foreground font-serif font-bold text-sm tracking-wide uppercase hover:bg-accent/90 transition-[background-color,box-shadow] shadow-sm flex items-center justify-center gap-2">
                      {submitting ? <>
                          <Loader2 className="size-4 animate-spin" />
                          <span>{copy.submittingBtn[lang]}</span>
                        </> : <>
                          <ShieldCheck className="size-4" />
                          <span>{copy.submitBtn[lang]}</span>
                        </>}
                    </Button>
                    <p className="text-center text-[11px] font-sans text-muted-foreground mt-3">
                      {copy.disclaimer[lang]}
                    </p>
                  </div>
                </form>}
            </div>
          </motion.div>
        </div>

        {/* ================= LOWER DIRECT CONTACT & CAMPUS MAP DECK ================= */}
        <div className="mt-14 lg:mt-18 pt-12 border-t border-border grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Direct Contact Matrix Card */}
          <div className="lg:col-span-6 rounded-xl border border-border bg-card p-6 sm:p-7 shadow-sm flex flex-col gap-5">
            <div className="border-b border-border pb-4">
              <span className="font-mono text-xs text-secondary font-semibold uppercase tracking-wider">
                {copy.contactEyebrow[lang]}
              </span>
              <h3 className="font-serif text-xl font-bold text-foreground mt-1">
                {copy.contactHeading[lang]}
              </h3>
            </div>

            <div className="flex flex-col gap-4 text-sm font-sans">
              {/* Real Hotline Item */}
              <div className="flex items-start gap-3.5">
                <div className="size-9 rounded-md bg-secondary/15 flex items-center justify-center text-secondary shrink-0 mt-0.5">
                  <Phone className="size-4" />
                </div>
                <div>
                  <span className="text-xs font-mono uppercase text-muted-foreground block">
                    {copy.hotlineLabel[lang]}
                  </span>
                  <a href="tel:0346386986" className="text-foreground font-bold text-base hover:text-secondary transition-colors">
                    0346 386 986
                  </a>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {copy.hotlineDesc[lang]}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="size-9 rounded-md bg-muted flex items-center justify-center text-primary shrink-0 mt-0.5">
                  <Mail className="size-4" />
                </div>
                <div>
                  <span className="text-xs font-mono uppercase text-muted-foreground block">
                    {copy.emailLabel[lang]}
                  </span>
                  <a href="mailto:contact@robotoacademy.com" className="text-foreground font-semibold hover:text-secondary transition-colors">
                    contact@robotoacademy.com
                  </a>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {copy.emailDesc[lang]}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="size-9 rounded-md bg-muted flex items-center justify-center text-primary shrink-0 mt-0.5">
                  <Clock className="size-4" />
                </div>
                <div>
                  <span className="text-xs font-mono uppercase text-muted-foreground block">
                    {copy.hoursLabel[lang]}
                  </span>
                  <p className="text-foreground font-semibold">
                    {copy.hoursWeekday[lang]}
                  </p>
                  <p className="text-foreground font-medium text-xs mt-0.5">
                    {copy.hoursSaturday[lang]}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {copy.hoursClosed[lang]}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Campus Map & Location Showcase */}
          <div className="lg:col-span-6 rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-mono text-xs text-secondary font-semibold uppercase tracking-wider">
                  {copy.campusEyebrow[lang]}
                </span>
                <h4 className="font-serif text-lg font-bold text-foreground">
                  {copy.campusHeading[lang]}
                </h4>
              </div>
              <div className="flex rounded-md border border-border p-0.5 bg-muted text-xs">
                <Button type="button" variant="ghost" onClick={() => setActiveCampus("main")} className={`h-auto px-2.5 py-1 rounded-xs font-mono text-xs transition-colors ${activeCampus === "main" ? "bg-card text-foreground font-bold shadow-2xs" : "text-muted-foreground hover:text-foreground"}`}>
                  {copy.tabMain[lang]}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setActiveCampus("west")} className={`h-auto px-2.5 py-1 rounded-xs font-mono text-xs transition-colors ${activeCampus === "west" ? "bg-card text-foreground font-bold shadow-2xs" : "text-muted-foreground hover:text-foreground"}`}>
                  {copy.tabWest[lang]}
                </Button>
              </div>
            </div>

            {/* Graphical Blueprint Map Box */}
            <div className="relative h-56 rounded-lg overflow-hidden border border-border bg-primary/95 text-primary-foreground p-4 flex flex-col justify-between">
              {/* Structural Grid lines */}
              <div className="pointer-events-none absolute inset-0 opacity-15" style={{
              backgroundImage: "linear-gradient(to right, #38bdf8 1px, transparent 1px), linear-gradient(to bottom, #38bdf8 1px, transparent 1px)",
              backgroundSize: "32px 32px"
            }} aria-hidden="true" />

              {/* Radar sweep accent */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-48 rounded-lg border border-secondary/20 pointer-events-none" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-24 rounded-lg border border-secondary/40 pointer-events-none animate-pulse" />

              {/* Pin overlay */}
              <div className="relative z-10 flex items-center justify-between text-xs font-mono">
                <span className="bg-primary/90 px-2 py-1 rounded-sm border border-secondary/30 text-secondary">
                  {activeCampus === "main" ? "HQ LAB 01" : "ANNEX LAB 02"}
                </span>
                <span className="text-primary-foreground/75">
                  LAT 21.0285 // LNG 105.8542
                </span>
              </div>

              <div className="relative z-10 flex items-center gap-3">
                <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-primary shadow-md">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <p className="font-serif font-bold text-sm text-primary-foreground">
                    {activeCampus === "main" ? copy.campusAddressMain[lang] : copy.campusAddressWest[lang]}
                  </p>
                  <p className="text-xs text-secondary mt-0.5">
                    {activeCampus === "main" ? copy.campusSuiteMain[lang] : copy.campusSuiteWest[lang]}
                  </p>
                </div>
              </div>
            </div>

            {/* Parking Guidance Strip */}
            <div className="flex items-start gap-3 p-3.5 rounded-lg bg-muted/60 border border-border text-xs text-muted-foreground">
              <Car className="size-4 text-secondary shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-semibold text-foreground block">
                  {copy.parkingTitle[lang]}
                </span>
                <p>{copy.parkingDesc[lang]}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}